/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { accessAPI, readFromLS } from "../../utils";

import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import Sidebar from "../sidebar";
import ImagenAutenticada from "./ImagenAutenticada";
import MenuUsuario from "../menuUsuario";
import logoHorizontalBlanco from "../../images/logoHorizontalBlanco.png";
import { useEstetica } from "../estetica";
import { NavLink } from "react-router-dom";
import Buscador from "../buscador/Buscador";

/**
 * Componente Header unificado para todas las aplicaciones.
 *
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Object} [props.cliente] - Información del cliente para mostrar branding
 * @param {string} [props.cliente.razonsocial] - Nombre del cliente
 * @param {number} [props.cliente.logoarchivoid] - ID del archivo del logo
 * @param {Object} [props.menuUsuarioProps={}] - Props adicionales para MenuUsuario
 * @param {boolean} [props.fetchInfoAccesos=true] - Si true, obtiene infoAccesos internamente
 * @param {Object} [props.infoAccesos] - Información de accesos (requerido si fetchInfoAccesos=false)
 * @param {React.ReactNode} [props.contenidoCentral] - Contenido personalizado para el centro del header
 * @param {React.ReactNode} [props.children] - Contenido adicional para mostrar debajo del header
 * @param {Array<{etiqueta: string, uri: string, icono?: string}>} [props.botonesMenuUsuario=[]] - Botones personalizados para el menú de usuario
 * @param {Array<{etiqueta: string, url: string, permiso?: string, badge?: number|string, badgeColor?: string}>} [props.botonera=[]] - Botones de navegación central del header.
 *   Un ítem con `permiso` (clave del catálogo de permisos) se oculta si el usuario no tiene al menos lectura.
 *   `badge`: número a mostrar junto a la etiqueta (no se muestra si es 0 o vacío), como los
 *   de NavLateral; `badgeColor`: "rojo" (por defecto) o cualquier color de App.css.
 *   Con `buscadorConfig` van en una barra propia debajo; sin buscador, en el centro de la barra superior.
 */
export default function Header({
  cliente: clienteProp = null,
  menuUsuarioProps = {},
  fetchInfoAccesos = true,
  infoAccesos: infoAccesosProp = null,
  contenidoCentral = null,
  children,
  botonesMenuUsuario = [],
  buscadorConfig,
  botonera: botoneraCompleta = []
}) {
  const estetica = useEstetica();
  const [infoAccesos, setInfoAccesos] = useState(infoAccesosProp);

  // Un ítem del menú (o un subítem) con `permiso: "clave"` solo se muestra si
  // el usuario tiene al menos lectura en ese permiso. Mientras no se conocen
  // los permisos, esos ítems no aparecen.
  const permitido = (item) =>
    !item.permiso || (infoAccesos?.permisos?.[item.permiso] ?? 0) >= 1;
  const botonera = botoneraCompleta
    .filter(permitido)
    .map((item) =>
      item.subitems
        ? { ...item, subitems: item.subitems.filter(permitido) }
        : item
    )
    .filter((item) => !item.subitems || item.subitems.length > 0);
  const [cliente, setCliente] = useState(clienteProp);
  const [menuVisible, setMenuVisible] = useState(false);

  // Sincronizar infoAccesos cuando viene por prop
  useEffect(() => {
    if (!fetchInfoAccesos && infoAccesosProp) {
      setInfoAccesos(infoAccesosProp);
      if (infoAccesosProp.clienteActual) {
        setCliente(infoAccesosProp.clienteActual);
      }
    }
  }, [fetchInfoAccesos, infoAccesosProp]);

  // Sincronizar cliente cuando viene por prop
  useEffect(() => {
    if (clienteProp) {
      setCliente(clienteProp);
    }
  }, [clienteProp]);

  useEffect(() => {
    if (!fetchInfoAccesos) return;

    let intentos = 0;
    const maxIntentos = 10;
    const intervalo = 300;

    const cargarInfoAccesos = () => {
      const token = readFromLS(process.env.REACT_APP_LS_LOGIN_TOKEN);

      if (!token) {
        if (intentos < maxIntentos) {
          intentos++;
          setTimeout(cargarInfoAccesos, intervalo);
        }
        return;
      }

      accessAPI(
        "GET",
        "usuario/info-accesos",
        null,
        (respuesta) => {
          setInfoAccesos(respuesta);
          if (respuesta.clienteActual) {
            setCliente(respuesta.clienteActual);
          }
        },
        (error) => {
          if (
            (error.status === 403 || error.status === 401) &&
            intentos < maxIntentos
          ) {
            intentos++;
            setTimeout(cargarInfoAccesos, intervalo);
          } else {
            console.error("Error cargando información de accesos:", error);
          }
        }
      );
    };

    cargarInfoAccesos();
  }, [fetchInfoAccesos]);

  // Si el usuario tiene acceso tipo Tablero (28), no renderizar el header.
  // Se compara accesoTipo y no accesoId: el id de la fila de Acceso lo asigna
  // la base y cambia según el organismo; el tipo es el número estable del rol.
  if (infoAccesos?.accesoTipo === 28) return null;

  // El logo del cabezal, en orden de preferencia: el horizontal blanco del
  // organismo, su horizontal a color, y recién ahí el de la plataforma. Va
  // sobre el color principal, así que el blanco es el que corresponde, pero
  // mostrar el logo del organismo aunque no sea el ideal es mejor que mostrar
  // el de Morfeo en el panel de un cliente.
  const logoCabezal =
    estetica?.logos?.horizontalBlanco ||
    estetica?.logos?.horizontal ||
    logoHorizontalBlanco;
  const altLogo = estetica?.nombre || "logo";

  const startContent = (
    <NavLink to="/" css={{ display: "flex", alignItems: "center" }}>
      <img
        css={{
          height: "33px",
          // Logos muy apaisados: se achican para no pasar de este ancho
          maxWidth: "170px",
          objectFit: "contain",
          objectPosition: "left center",
          alignSelf: "center",
          marginLeft: "10px"
        }}
        src={logoCabezal}
        alt={altLogo}
      />
    </NavLink>
  );

  // Botones de navegación de escritorio (en mobile van en el sidebar)
  const botonesNavegacion = botonera.map((boton, index) =>
    boton.subitems ? (
      <BotonSubmenuDesktop key={index} boton={boton} />
    ) : (
      <NavLink key={index} to={boton.url} end css={{ position: "relative" }}>
        <span>{boton.etiqueta}</span>
        <BadgeNavegacion boton={boton} />
      </NavLink>
    )
  );

  // Sin buscador, la navegación va en el centro de la barra superior en vez
  // de en su propia barra debajo
  const navegacionEnBarra = !buscadorConfig && botonera.length > 0;

  // El centro de la barra superior es la búsqueda (la navegación vive en su
  // propia barra, debajo). Ancha y centrada; en mobile va en el sidebar.
  const centerContent = buscadorConfig ? (
    <div
      css={{
        display: "none",
        "@media (min-width: 992px)": {
          display: "flex",
          alignItems: "center",
          gap: "0.5em",
          // Ancho ideal; si no entra (p. ej. con el asistente abierto, que
          // achica la app sin cambiar el vw) se encoge antes que el logo y
          // el menú de usuario
          width: "42vw",
          maxWidth: "640px",
          minWidth: 0
        },
        "& .p-autocomplete": { width: "100%", flex: 1 },
        "& .p-autocomplete-input": { width: "100% !important" }
      }}
    >
      <Buscador config={buscadorConfig} />
    </div>
  ) : navegacionEnBarra ? (
    <nav
      css={{
        display: "none",
        "@media (min-width: 992px)": {
          display: "flex",
          alignItems: "center",
          gap: "0.5em",
          minWidth: 0
        },
        "@media (min-width: 1024px)": {
          gap: "1em"
        },
        fontSize: "0.85rem",
        whiteSpace: "nowrap"
      }}
    >
      {botonesNavegacion}
    </nav>
  ) : contenidoCentral ? (
      <div
        css={{
          display: "none",
          "@media (min-width: 992px)": {
            display: "block"
          }
        }}
      >
        {contenidoCentral}
      </div>
    ) : null;

  // Determinar las props finales para MenuUsuario
  const menuUsuarioFinalProps = {
    fetchInterno: !fetchInfoAccesos && !infoAccesos ? true : !fetchInfoAccesos,
    infoAccesos: infoAccesos,
    botonesCustom: botonesMenuUsuario,
    ...menuUsuarioProps
  };

  const endContent = (
    <>
      {/* Contenido normal para pantallas grandes */}
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          "@media (min-width: 1024px)": {
            gap: "15px"
          },
          "@media (max-width: 991px)": {
            display: "none"
          }
        }}
      >
        {cliente?.razonsocial && (
          <span
            css={{
              color: "white",
              fontSize: "1rem",
              fontWeight: "500"
            }}
          >
            {cliente.razonsocial}
          </span>
        )}
        {cliente?.logoarchivoid && (
          <ImagenAutenticada
            endpoint="cliente/cliente/logo"
            alt={`Logo ${cliente.razonsocial || "cliente"}`}
            altura="40px"
            anchoMaximo="120px"
          />
        )}
        <MenuUsuario {...menuUsuarioFinalProps} />
      </div>

      {/* Botón hamburguesa para pantallas pequeñas */}
      <Button
        icon="pi pi-bars"
        onClick={() => setMenuVisible(true)}
        text
        css={{
          color: "white !important",
          fontSize: "1.5rem !important",
          display: "none",
          "@media (max-width: 991px)": {
            display: "flex"
          },
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.1) !important"
          }
        }}
      />
    </>
  );

  return (
    <>
      <header
        css={{
          width: "100%",
          height: "70px",
          background: "var(--principal)",
          // Proyecta una sombra sutil sobre la barra de navegación de abajo;
          // el zIndex hace que la sombra se pinte encima de ella
          boxShadow: "0px 4px 14px 0px rgba(13,13,19,0.12)",
          position: "relative",
          zIndex: 10
        }}
      >
        <Toolbar
          css={{
            width: "100%",
            maxWidth: "1440px",
            marginLeft: "auto",
            marginRight: "auto",
            backgroundColor: "var(--principal)",
            border: "none",
            padding: "11px",
            borderRadius: "0px",
            height: "70px",
            // PrimeReact deja que los grupos bajen de línea; acá el que cede
            // lugar es la búsqueda del centro
            flexWrap: "nowrap",
            // El logo y el menú de usuario tienen anchos distintos: si los
            // grupos laterales no reparten el espacio en partes iguales, la
            // búsqueda del centro queda corrida
            "& .p-toolbar-group-start": { flex: 1, justifyContent: "flex-start" },
            "& .p-toolbar-group-end": { flex: 1, justifyContent: "flex-end" },
            "& .p-toolbar-group-center": { flex: "0 1 auto", minWidth: 0 }
          }}
          start={startContent}
          center={centerContent}
          end={endContent}
        />
      </header>

      {/* Barra de navegación: menos alta y de ancho completo, debajo de la
          barra principal. En mobile la navegación va en el sidebar. */}
      {botonera.length > 0 && !navegacionEnBarra && (
        <div
          css={{
            display: "none",
            "@media (min-width: 992px)": { display: "block" },
            width: "100%",
            background: "var(--principal)",
            position: "relative",
            // Por encima de la botonera de sección (zIndex 1), que al entrar
            // se anima saliendo desde atrás de esta barra
            zIndex: 5
          }}
        >
          <nav
            css={{
              maxWidth: "1440px",
              margin: "0 auto",
              // Alto mínimo y no fijo: si los botones no entran en una línea
              // (p. ej. con el asistente abierto) bajan a la siguiente. Cada
              // etiqueta sigue sin cortarse por el nowrap.
              minHeight: "42px",
              padding: "0.4em 1em",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.4em 0.5em",
              fontSize: "0.85rem",
              whiteSpace: "nowrap",
              "@media (min-width: 1024px)": {
                gap: "0.4em 1em"
              }
            }}
          >
            {botonesNavegacion}
          </nav>
        </div>
      )}

      {/* Contenido adicional debajo del header (ej: navegación) */}
      {children}

      {/* Sidebar para menú móvil */}
      <Sidebar
        visible={menuVisible}
        position="right"
        onHide={() => setMenuVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
        css={{
          "&.p-sidebar": {
            backgroundColor: "var(--principal)"
          },
          "& .p-sidebar-header": {
            justifyContent: "center",
            "& .p-sidebar-close": {
              color: "white",
              position: "absolute",
              right: "1rem"
            }
          },
          "& .p-sidebar-content": {
            display: "flex",
            flexDirection: "column",
            height: "100%"
          }
        }}
        header={
          <NavLink
            to="/"
            onClick={() => setMenuVisible(false)}
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              alignSelf: "stretch",
              margin: "-1.25rem 0",
              padding: "1.25rem 0"
            }}
          >
            <img
              src={logoCabezal}
              alt={altLogo}
              css={{ height: "33px", maxWidth: "170px", objectFit: "contain" }}
            />
          </NavLink>
        }
      >
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            paddingTop: "10px",
            color: "white",
            "& span, & i": { color: "white" },
            "& .p-divider": { display: "none" },
            "& a": { color: "white" },
            // El badge del link activo sigue con texto blanco
            "& a.active span:not(.badge-navegacion)": { color: "var(--principal)" },
            "& div:hover": {
              backgroundColor: "transparent !important"
            },
          }}
        >
          {/* Buscador */}
          {buscadorConfig && (
            <div
              css={{
                paddingTop: "15px",
                paddingBottom: "20px",
                marginBottom: "20px",
                "& .p-autocomplete": {
                  width: "100%"
                },
                "& .p-autocomplete-input": {
                  width: "100% !important"
                }
              }}
            >
              <Buscador
                config={buscadorConfig}
                onNavegar={() => setMenuVisible(false)}
              />
            </div>
          )}

          {/* Botonera de navegación */}
          {botonera.length > 0 && (
            <nav
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "1em",
                paddingBottom: "20px",
                marginBottom: "20px"
              }}
            >
              {botonera.map((boton, index) =>
                boton.subitems ? (
                  <BotonSubmenuMobile
                    key={index}
                    boton={boton}
                    onNavegar={() => setMenuVisible(false)}
                  />
                ) : (
                  <NavLink
                    key={index}
                    to={boton.url}
                    end
                    onClick={() => setMenuVisible(false)}
                    css={{
                      display: "block",
                      textAlign: "center"
                    }}
                  >
                    {/* En el sidebar el badge va pegado a la etiqueta */}
                    <span css={{ position: "relative" }}>
                      {boton.etiqueta}
                      <BadgeNavegacion boton={boton} />
                    </span>
                  </NavLink>
                )
              )}
            </nav>
          )}

          {/* Contenido central custom */}
          {contenidoCentral && !botonera.length && (
            <div
              css={{
                paddingBottom: "20px",
                marginBottom: "20px"
              }}
              onClick={() => setMenuVisible(false)}
            >
              {contenidoCentral}
            </div>
          )}

          {/* Logo y nombre del cliente */}
          {(cliente?.razonsocial || cliente?.logoarchivoid) && (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "12px",
                paddingBottom: "20px",
                marginBottom: "20px"
              }}
            >
              {cliente?.logoarchivoid && (
                <ImagenAutenticada
                  endpoint="cliente/cliente/logo"
                  alt={`Logo ${cliente.razonsocial || "cliente"}`}
                  altura="50px"
                  anchoMaximo="150px"
                />
              )}
              {cliente?.razonsocial && (
                <span
                  css={{
                    fontSize: "1.1rem",
                    fontWeight: "500",
                    textAlign: "center",
                    color: "white"
                  }}
                >
                  {cliente.razonsocial}
                </span>
              )}
            </div>
          )}

          {/* Menú de usuario en modo expandido */}
          <div
            css={{
              marginTop: "auto",
              textAlign: "center",
              "& > div > div > div": {
                justifyContent: "center"
              },
              "& div, & span, & p": {
                color: "white !important"
              },
              "& > div > div:first-child": {
                marginBottom: "20px"
              }
            }}
          >
            <MenuUsuario
              modoExpandido={true}
              {...menuUsuarioFinalProps}
              onAccionEjecutada={() => setMenuVisible(false)}
            />
          </div>
        </div>
      </Sidebar>
    </>
  );
}

// Badge de un botón de la navegación (`badge`/`badgeColor`): la misma
// píldora que los badges de NavLateral, en la esquina superior derecha del
// botón (el padre tiene que tener position: relative) y con borde blanco
function BadgeNavegacion({ boton }) {
  const { badge, badgeColor } = boton;
  if (badge == null || badge === "" || badge === 0 || badge === "0") return null;
  return (
    <span
      className="badge-navegacion"
      css={{
        position: "absolute",
        top: 0,
        right: 0,
        transform: "translate(50%, -50%)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "1.6em",
        height: "1.6em",
        padding: "0 0.4em",
        border: "1px solid white",
        borderRadius: "999px",
        pointerEvents: "none",
        backgroundColor: `var(--${badgeColor || "rojo"})`,
        color: "white",
        fontSize: "0.75em",
        fontWeight: 700,
        lineHeight: 1,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.2)"
      }}
    >
      {badge}
    </span>
  );
}

function BotonSubmenuDesktop({ boton }) {
  const menuRef = useRef(null);
  const [abierto, setAbierto] = useState(false);

  // Lista plana de items (sin agruparlos en un submenu) para evitar que
  // PrimeReact renderice un .p-submenu-header vacío que agrega margen arriba.
  const items = boton.subitems.map((sub) => ({
    label: sub.etiqueta,
    template: () => (
          <NavLink
            to={sub.url}
            end
            onClick={(e) => {
              // Cortamos la propagación para que el <li> de PrimeReact no
              // ejecute su onItemClick, que llama preventDefault() cuando el
              // item no tiene url y cancelaría el Command/Ctrl+click (abrir en
              // nueva pestaña). NavLink se encarga de la navegación SPA en el
              // clic normal y deja pasar el modificador para la nueva pestaña.
              e.stopPropagation();
              menuRef.current?.hide(e);
            }}
            css={{
              display: "flex",
              alignItems: "center",
              padding: "0.65rem 1rem",
              color: "#495057",
              textDecoration: "none",
              fontSize: "0.9rem",
              transition: "background-color 0.15s",
              "& span": { color: "inherit" },
              "&:hover": { backgroundColor: "#f8f9fa" },
              "&.active": {
                color: "var(--principal)",
                fontWeight: 600,
                backgroundColor: "#eff6ff"
              },
              "&.active span": { color: "var(--principal)" }
            }}
          >
            <span>{sub.etiqueta}</span>
          </NavLink>
        )
  }));

  return (
    <>
      <button
        type="button"
        onClick={(e) => menuRef.current?.toggle(e)}
        css={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "white",
          font: "inherit",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4em",
          padding: 0
        }}
      >
        <span>{boton.etiqueta}</span>
        <i
          className={abierto ? "pi pi-chevron-up" : "pi pi-chevron-down"}
          css={{ fontSize: "0.7rem" }}
        />
      </button>
      <Menu
        ref={menuRef}
        model={items}
        popup
        appendTo={typeof document !== "undefined" ? document.body : undefined}
        onShow={() => setAbierto(true)}
        onHide={() => setAbierto(false)}
        css={{
          minWidth: "200px",
          marginTop: "8px",
          padding: "0.25rem 0",
          "& .p-menuitem": {
            margin: 0
          },
          "& .p-menuitem-content": {
            padding: 0,
            background: "transparent !important"
          }
        }}
      />
    </>
  );
}

function BotonSubmenuMobile({ boton, onNavegar }) {
  const [abierto, setAbierto] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        css={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "white",
          font: "inherit",
          width: "100%",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.4em"
        }}
      >
        <span>{boton.etiqueta}</span>
        <i
          className={abierto ? "pi pi-chevron-up" : "pi pi-chevron-down"}
          css={{ fontSize: "0.7rem" }}
        />
      </button>
      {abierto && (
        <nav
          css={{
            display: "flex",
            flexDirection: "column",
            gap: "0.75em",
            marginTop: "0.75em",
            paddingTop: "0.5em"
          }}
        >
          {boton.subitems.map((sub, i) => (
            <NavLink
              key={i}
              to={sub.url}
              end
              onClick={onNavegar}
              css={{
                display: "block",
                textAlign: "center",
                fontSize: "0.95em",
                opacity: 0.85
              }}
            >
              <span>{sub.etiqueta}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
